import type { ReactNode } from 'react';
import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import styles from './States.module.css';

import { useT, tNow } from '@/i18n/languageStore';

export interface EmptyStateProps {
  icon?: IconName;
  title: string;
  /** What happens here, or what to do next. */
  children?: ReactNode;
  /** The next step (a Button or link). */
  action?: ReactNode;
}

/** An empty list says why it is empty and offers the next step. */
export function EmptyState({ icon, title, children, action }: EmptyStateProps) {
  const t = useT();
  return (
    <div className={styles.empty}>
      {icon ? (
        <span className={styles.emptyIcon}>
          <Icon name={icon} size={28} />
        </span>
      ) : null}
      <h2 className={styles.emptyTitle}>{t(title)}</h2>
      {children ? <p className={styles.emptyText}>{children}</p> : null}
      {action ? <div className={styles.emptyAction}>{action}</div> : null}
    </div>
  );
}

export interface ErrorStateProps {
  title: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
}

/** A failed load, announced to screen readers, with a way to try again. */
export function ErrorState({
  title,
  description,
  onRetry,
  retryLabel = tNow('Try again'),
}: ErrorStateProps) {
  const t = useT();
  return (
    <div className={styles.error} role="alert">
      <span className={styles.errorIcon}>
        <Icon name="warning-triangle" size={24} />
      </span>
      <p className={styles.errorTitle}>{t(title)}</p>
      {description ? <p className={styles.errorText}>{description}</p> : null}
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t(retryLabel)}
        </Button>
      ) : null}
    </div>
  );
}

/** A loading indicator with a name ("Loading messages"). */
export function Spinner({ label = tNow('Loading'), size = 24 }: { label?: string; size?: number }) {
  const t = useT();
  return (
    <span className={styles.spinnerWrap} role="status" aria-label={t(label)}>
      <span className={styles.spinner} style={{ ['--spinner-size' as string]: `${size}px` }} />
    </span>
  );
}

/** Placeholder lines while content loads; hidden from screen readers (pair with a Spinner or aria-busy). */
export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className={styles.skeleton} aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} className={styles.skeletonLine} data-skeleton-line />
      ))}
    </div>
  );
}

export interface ProgressBarProps {
  /** 0–1 */
  value: number;
  label: string;
  tone?: 'success' | 'primary' | 'secondary';
}

/** mobile/src/components/ui/ProgressBar.tsx, as a real progress bar. */
export function ProgressBar({ value, label, tone = 'success' }: ProgressBarProps) {
  const t = useT();
  const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div
      className={styles.track}
      role="progressbar"
      aria-label={t(label)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      data-tone={tone}
    >
      <span className={styles.fill} style={{ width: `${percent}%` }} />
    </div>
  );
}
